import React, { useMemo, useRef, useState } from 'react'
import ReactSwipe from 'react-swipe'
import { ImageData } from '../../utils/imagesS3'
import styles from './responsiveSliderBanner.module.scss'

export type SizesVariant =
  | 'banner_promociones'
  | 'banner_home'
  | 'banner_neumaticos'
  | 'banner_landings'

interface DotsProps {
  images: ResponsiveImageData[]
  current: number
  onClick: (i: number) => void
}

const Dots = ({ images, current, onClick }: DotsProps) => {
  return (
    <React.Fragment>
      {images.map((_item, i) => (
        <div
          key={i}
          className={
            current === i ? `${styles.dot} ${styles.active}` : styles.dot
          }
          onClick={() => onClick(i)}
        />
      ))}
    </React.Fragment>
  )
}

interface ArrowsProps {
  onPrev: () => void
  onNext: () => void
}

const Arrows = ({ onPrev, onNext }: ArrowsProps) => {
  return (
    <div className={styles.arrows_wrapper}>
      <div className={styles.leftArrow} onClick={() => onPrev()} />
      <div className={styles.rightArrow} onClick={() => onNext()} />
    </div>
  )
}

type ResponsiveImageData = { desktop: ImageData; mobile: ImageData }
const onError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
  event.currentTarget.style.display = 'none'
}
const PictureMaker = ({ item }: { item: ResponsiveImageData }) => (
  <picture>
    <source srcSet={item.mobile.url} media="(max-width: 767px)" />
    <img
      src={item.desktop.url}
      alt={item.desktop.alt || item.mobile.alt}
      onError={onError}
    />
  </picture>
)

interface ResponsiveSliderBannerContentsProps {
  images: ResponsiveImageData[]
  sizesVariant: SizesVariant
}

const Slide = ({
  item,
  extraClass = '',
}: {
  item: ResponsiveImageData
  extraClass?: string
}) => (
  <div className={`${styles.image_wrapper} ${extraClass}`}>
    {item.desktop.link || item.mobile.link ? (
      <a href={item.desktop.link || item.mobile.link}>
        <PictureMaker item={item} />
      </a>
    ) : (
      <PictureMaker item={item} />
    )}
  </div>
)

const ResponsiveSliderBannerContents = ({
  images,
  sizesVariant,
}: ResponsiveSliderBannerContentsProps) => {
  const [position, setPosition] = useState(0)
  const reactSwipe = useRef(null)

  const swipe = useMemo(() => {
    if (!images.length) {
      return null
    }

    const swipeOptions = {
      speed: 300,
      auto: 4000,
      continuous: true,
      widthOfSiblingSlidePreview: 0,
      callback: (index: number) => {
        setPosition(index % images.length)
      },
    }
    return (
      <ReactSwipe
        ref={(el) => {
          reactSwipe.current = el
        }}
        swipeOptions={swipeOptions}>
        {images.map((item, i) => (
          <Slide key={i} item={item} />
        ))}
      </ReactSwipe>
    )
  }, [images])

  const handleChange = (id: number) => {
    reactSwipe.current.slide(id)
  }

  const prev = () => {
    reactSwipe.current.prev()
  }

  const next = () => {
    reactSwipe.current.next()
  }

  return (
    <React.Fragment>
      {images.length > 0 &&
        (sizesVariant ? (
          <div>
            <div className={styles[sizesVariant]}></div>
            <Slide key="slide0" item={images[0]} extraClass={styles.floating} />
          </div>
        ) : (
          <Slide key="slide0" item={images[0]} />
        ))}
      {images.length > 1 && <div className={styles.floating}>{swipe}</div>}
      {images.length > 1 && <Arrows onPrev={prev} onNext={next} />}
      {images.length > 1 && (
        <div className={styles.dots}>
          <Dots current={position} images={images} onClick={handleChange} />
        </div>
      )}
    </React.Fragment>
  )
}

interface Props {
  desktopImages: ImageData[]
  mobileImages: ImageData[]
  sliderClass?: string
  sizesVariant?: SizesVariant
}

const ResponsiveSliderBanner = ({
  desktopImages,
  mobileImages,
  sliderClass,
  sizesVariant,
}: Props) => {
  const images = useMemo(() => {
    const nImages =
      desktopImages.length < mobileImages.length
        ? desktopImages.length
        : mobileImages.length
    const images = [] as ResponsiveImageData[]
    for (let i = 0; i < nImages; i++) {
      images.push({ desktop: desktopImages[i], mobile: mobileImages[i] })
    }
    return images
  }, [desktopImages, mobileImages])

  const classSlider = sliderClass || styles.sliderbanner_wrapper_default

  if (!images.length) {
    return null
  }

  return (
    <div className={`${styles.sliderbanner_wrapper} ${classSlider}`}>
      <ResponsiveSliderBannerContents
        sizesVariant={sizesVariant}
        images={images}
      />
    </div>
  )
}

export default ResponsiveSliderBanner
